import { z } from 'zod';
import { Ownership, Sentiment, TimestampLike } from './common.js';

export const AddedMethod = z.enum([
  'intake',
  'manual',
  'ai_suggested_accepted',
  'semrush_import',
]);
export type AddedMethod = z.infer<typeof AddedMethod>;

export const TrackedUrlDoc = z.object({
  url: z.string().url(),
  domain: z.string().min(1),
  title: z.string().default(''),
  snippet: z.string().default(''),
  ownership: Ownership,
  sentiment: Sentiment,
  addedAt: TimestampLike,
  addedBy: z.string().min(1),
  addedMethod: AddedMethod,
  /**
   * PM's note on why this row is classified the way it is.
   *
   * Existed and was written only by the manual "+ Add URL" modal; intake never
   * read or wrote it, so the reasoning behind a judgement call lived nowhere.
   * Now editable at intake and fed to the narrative generator, which otherwise
   * has to infer intent from a position number — "this is the client's father,
   * suppress but do not claim" is not derivable from ranking data.
   */
  notes: z.string().max(500).optional(),
  /**
   * Carries a classification nobody has actually assessed.
   *
   * Both axes are required on this document, so "not yet reviewed" cannot be
   * expressed by leaving sentiment empty. The Semrush importer had to write
   * something and wrote `negative` for everything; the 2026-08-14 correction
   * pass moved those to `neutral`, which is equally a guess. This flag is what
   * separates a value a person chose from a placeholder standing in for one,
   * and it is what the intake queue filters on. Cleared the moment a human
   * grades the row.
   */
  needsReview: z.boolean().optional(),
  /**
   * False when this result is about someone else who shares the subject's name.
   *
   * Absent means "is our subject" — the overwhelming default, and it keeps
   * every document written before this field existed correct without a
   * migration.
   *
   * Deliberately INDEPENDENT of sentiment. Ed Young Jr's father ranks for the
   * same query, and the client wants those results pushed down: they are not
   * our subject AND they are negative, and the report should say so. Collapsing
   * this into sentiment would force a choice between hiding results the client
   * is paying us to suppress and claiming they are about him.
   *
   * Never written to `domainClassifications`. Whether a facebook.com profile is
   * the right person is a fact about this subject, not about facebook.com —
   * learning it globally would mislabel the next client on that domain.
   */
  isSubject: z.boolean().optional(),
  isActive: z.boolean().default(true),
  // Focus URLs are what appears on the position chart + client portal.
  // A PM classifies all top-100 URLs at intake (table/audit view), but only
  // a subset are "actively watched" — see computeDefaultFocus() below.
  isFocus: z.boolean().default(false),
});
export type TrackedUrlDoc = z.infer<typeof TrackedUrlDoc>;
